import React, { useCallback, useEffect, useMemo } from "react";
import { ROUTE_TYPES } from "../../../classes/constants/routeConstants";
import { useRemoteRenderContext } from "../../../classes/contexts/RemoteRenderContext";
import { getTargetTypeDisplayLabel } from "../../../classes/enums/SppEnum";
import { ToolsType } from "../../../classes/enums/ToolsEnum";
import { SpinesimSurgeryViewType } from "../../../classes/enums/ViewEnum";
import { convertColorToRGB } from "../../../classes/utils/ParsingUtils";
import "./AlignSpineOverlayTools.css";

const AlignSpineOverlayTools = ({
  spinesimSurgeryViewType = SpinesimSurgeryViewType.ALL,
  toolsConfigs = [],
  targetType = "gap", // Add targetType prop
  currentPlanId,
  disabled = false,
  activeTools = {}, // Accept state as prop
  setActiveTools = () => {}, // Accept setter as prop
}) => {
  const { client: remoteRenderClient } = useRemoteRenderContext();

  // Filter align tools and dynamically update the Target tool name
  const alignSpineOverlayTools = useMemo(() => {
    const tools = toolsConfigs.filter(
      tool => tool.category === ToolsType.ALIGN
    );

    // Map over tools and replace "Target" with the appropriate target type
    return tools.map(tool => {
      if (tool.name === "Target" || tool.shortName === "Target") {
        const targetLabel = getTargetTypeDisplayLabel(targetType);
        return {
          ...tool,
          name: tool.name === "Target" ? targetLabel : tool.name,
          shortName: tool.shortName === "Target" ? targetLabel : tool.shortName,
          // Keep the original key for backend communication
          originalName: tool.name,
          originalShortName: tool.shortName,
        };
      }
      return tool;
    });
  }, [toolsConfigs, targetType]);

  const defaultState = false;

  useEffect(() => {
    // Only initialize tools that don't already have a state
    const newTools = {};
    const toolsToInitialize = [];

    alignSpineOverlayTools.forEach(tool => {
      if (activeTools[tool.key] === undefined) {
        // Tool doesn't have state yet, initialize it
        newTools[tool.key] = defaultState;
        toolsToInitialize.push(tool);
      }
    });

    // Only update state if there are new tools to add
    if (Object.keys(newTools).length > 0) {
      setActiveTools(prevState => ({
        ...prevState,
        ...newTools,
      }));

      // Only send RPC calls for newly initialized tools
      if (!remoteRenderClient || disabled) {
        return;
      }

      toolsToInitialize.forEach(async tool => {
        try {
          await remoteRenderClient.setToolVisibility(
            currentPlanId,
            tool.key,
            defaultState,
            SpinesimSurgeryViewType.ALL,
            null,
            ROUTE_TYPES.ALIGN.toLowerCase()
          );
        } catch (error) {}
      });
    }
  }, [
    alignSpineOverlayTools,
    activeTools,
    currentPlanId,
    defaultState,
    disabled,
    remoteRenderClient,
    spinesimSurgeryViewType,
    setActiveTools,
  ]);

  const onToolSelected = useCallback(
    async (key, shortName) => {
      if (disabled) return;

      const newState = !activeTools[key];
      setActiveTools(prevState => ({
        ...prevState,
        [key]: newState,
      }));

      try {
        if (!remoteRenderClient) return;
        await remoteRenderClient.setToolVisibility(
          currentPlanId,
          key,
          newState,
          SpinesimSurgeryViewType.ALL,
          null,
          ROUTE_TYPES.ALIGN.toLowerCase()
        );
      } catch (error) {
        setActiveTools(prevState => ({
          ...prevState,
          [shortName]: !newState,
        }));
      }
    },
    [activeTools, currentPlanId, remoteRenderClient, disabled]
  );

  return (
    <div
      className="Align-spine-overlay-tools-container"
      style={disabled ? { pointerEvents: "none", opacity: 0.5 } : undefined}
    >
      {alignSpineOverlayTools.map(tool => (
        <div className="Align-spine-overlay-tool" key={tool.key}>
          <AlignSpineOption
            key={tool.key}
            label={tool.name}
            shortLabel={tool.shortName}
            toolKey={tool.key}
            color={tool.color}
            isActive={activeTools[tool.key]}
            onToolSelected={onToolSelected}
            disabled={disabled}
          />
        </div>
      ))}
    </div>
  );
};

const AlignSpineOption = React.memo(
  ({
    label,
    shortLabel,
    toolKey,
    color,
    isActive,
    onToolSelected,
    disabled = false,
  }) => {
    const rgbColor = convertColorToRGB(color);

    return (
      <div
        className="Align-spine-overlay-option App-text-small"
        onClick={() => {
          if (!disabled) {
            onToolSelected(toolKey, shortLabel);
          }
        }}
      >
        <div
          className={`App-option-circle ${isActive ? "active" : ""}`}
          style={{
            borderColor: rgbColor,
            backgroundColor: isActive ? rgbColor : "transparent",
          }}
        ></div>
        <span style={{ color: rgbColor }}>
          {label}
          {shortLabel ? ` (${shortLabel})` : ""}
        </span>
      </div>
    );
  }
);

export default AlignSpineOverlayTools;
